import { DateTime } from '@ksyos/date-time';
import { Alert } from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import type { ICustomControlProcedureReadProps } from '../../interfaces/';
import { getServiceLabel } from '../../../../../../common/customControls/getServiceLabel';
import { CustomControlStatus } from '../../../../../../common/enums/customControlStatus';
import { ProcedureStatus } from '../../../../../../common/enums/procedureStatus';
import { getDisplayName } from '../../../../../../common/utils/getDisplayName';
import { translateKey } from '../../../../../translate';
import { useEpisodeContext } from '../../../../../utils/hooks/useEpisodeContext';
import { SsoEmbloomGradingButton } from '../../../../elements/carePathButtons/SsoEmbloomGradingButton';
import { TakeProcedureButton } from '../../../../elements/carePathButtons/TakeProcedureButton';
import { TakeProcedureFromOrganizationButton } from '../../../../elements/carePathButtons/TakeProcedureFromOrganizationButton';
import { ButtonContainer, Loader } from '../../../../elements/common';
import { InlineReadElement } from '../../../../elements/inlineReadElement/InlineReadElement';
import { ProcessContainer } from '../../../episodes/common';
import { ProcessItemFooter } from '../../../episodes/ProcessItemFooter';
import { ProcessItemHeader } from '../../../episodes/ProcessItemHeader';
import { CustomControlCollapseBody } from '../../utils/v1/CustomControlCollapseBody';
import { ProcedureThumbnails } from '../../utils/v1/ProcedureThumbnails';

export function GgzoEmblV1ProcedureRead(props: ICustomControlProcedureReadProps) {
    const { patient } = useEpisodeContext();
    const maxDiagnosticReportPollAttempts = 10;
    const [loadingReportStatus, setLoadingReportStatus] = useState<'loading' | 'takingLonger' | 'failed'>('loading');

    useEffect(() => {
        pollDiagnosticReport();
    }, []);

    return (
        <ProcessContainer isActiveActivity={props.itemState.initialIsOpen}>
            <ProcessItemHeader
                date={props.process.procedure && props.process.procedure.dateSent}
                isFirstItem={props.isFirstItem}
                isFirstProcess={props.itemState.isFirstProcess}
                isItemOpen={props.itemState.isOpen}
                onToggle={async (isOpened) =>
                    await props.updateItemState({
                        ...props.itemState,
                        isOpen: isOpened || !!props.itemState.initialIsOpen,
                    })
                }
                organizationName={props.process.requesterOrganization?.name}
                practitionerName={getDisplayName(patient)}
                process={props.process}
                serviceType={props.process.serviceType}
                showProcessIcon
                title={getTitle()}
            />

            <CustomControlCollapseBody isOpen={props.itemState.isOpen}>
                {props.procedure.customControlStatus === CustomControlStatus.withPatient && (
                    <Alert appearance="info">{getDisplayName(patient)} heeft het onderzoek nog niet ingevuld</Alert>
                )}

                {props.procedure.status === ProcedureStatus.completed &&
                    props.process.diagnosticReports.length === 0 && (
                        <>
                            {props.procedure.customControlData &&
                                props.procedure.customControlData.isGradingCompleted &&
                                renderLoadingReport()}
                            {props.procedure.customControlData &&
                                !props.procedure.customControlData.isGradingCompleted && (
                                    <>
                                        <Alert appearance="success">
                                            {getDisplayName(patient)} heeft de vragenlijst ingevuld op{' '}
                                            {props.procedure.customControlData.submitDatetime
                                                ? `${DateTime.from(
                                                      props.procedure.customControlData.submitDatetime
                                                  ).format('DD-MM-YYYY')}`
                                                : '-'}
                                        </Alert>
                                        <ButtonContainer
                                            alignRight
                                            buttons={[
                                                <TakeProcedureButton
                                                    procedure={props.procedure}
                                                    reloadProcess={props.reloadProcess}
                                                    request={props.request}
                                                />,
                                                <TakeProcedureFromOrganizationButton
                                                    procedure={props.procedure}
                                                    reloadProcess={props.reloadProcess}
                                                    request={props.request}
                                                />,
                                                <SsoEmbloomGradingButton request={props.request} />,
                                            ]}
                                        />
                                    </>
                                )}
                        </>
                    )}

                {props.procedure.status === ProcedureStatus.completed &&
                    props.process.diagnosticReports.length === 1 && <ProcedureThumbnails {...props} />}
            </CustomControlCollapseBody>

            {!props.itemState.initialIsOpen && (
                <ProcessItemFooter
                    isItemOpen={props.itemState.isOpen}
                    labelLess={translateKey('procedure.hideProcedure')}
                    labelMore={translateKey('procedure.showProcedure')}
                    onToggle={async (isOpened) => await props.updateItemState({ ...props.itemState, isOpen: isOpened })}
                />
            )}
        </ProcessContainer>
    );

    function getTitle() {
        const label =
            props.request.customControlData.vragenlijstLabel ??
            getServiceLabel(props.process.code, 'procedure', props.process.name);

        return props.procedure.customControlData?.isNameting ? `${label} Nameting` : label;
    }

    function renderLoadingReport() {
        if (loadingReportStatus === 'loading' || loadingReportStatus === 'takingLonger') {
            return (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <div style={{ marginBottom: '16px' }}>
                        <Loader />
                    </div>

                    {loadingReportStatus === 'loading' && (
                        <InlineReadElement color="muted">
                            {translateKey('embloomQuestionnaires.loadingReport')}
                        </InlineReadElement>
                    )}

                    {loadingReportStatus === 'takingLonger' && (
                        <>
                            <InlineReadElement color="muted">
                                {translateKey('embloomQuestionnaires.loadingReportTakesLonger')}
                            </InlineReadElement>
                            <InlineReadElement color="muted">
                                {translateKey('embloomQuestionnaires.reportOnItsWay')}
                            </InlineReadElement>
                        </>
                    )}
                </div>
            );
        }

        return (
            <Alert appearance="warning">
                {translateKey('embloomQuestionnaires.loadingReportFailed')}
                <br />
                {translateKey('embloomQuestionnaires.contactServiceDesk')}
            </Alert>
        );
    }

    function pollDiagnosticReport(attempt: number = 0) {
        const shouldPoll =
            props.procedure.customControlData &&
            props.procedure.customControlData.isGradingCompleted &&
            props.process.diagnosticReports.length === 0 &&
            attempt <= maxDiagnosticReportPollAttempts;

        if (shouldPoll) {
            props.reloadProcess!();
            if (3 < attempt && attempt < maxDiagnosticReportPollAttempts) {
                setLoadingReportStatus('takingLonger');
            } else if (attempt >= maxDiagnosticReportPollAttempts) {
                setLoadingReportStatus('failed');
            }
            setTimeout(() => pollDiagnosticReport(attempt + 1), 1000);
        }
    }
}
